import { Calendar, Monitor, Search, Smartphone, Tablet, X } from 'lucide-react';
import clsx from 'clsx';
import { DATE_RANGES, DEVICE_TYPES } from '../../utils/constants';
import type { SessionFilterState } from '../../pages/SessionsPage';

interface SessionFiltersProps {
  filters: SessionFilterState;
  onChange: (filters: SessionFilterState) => void;
}

export default function SessionFilters({ filters, onChange }: SessionFiltersProps) {
  function update(partial: Partial<SessionFilterState>) {
    onChange({ ...filters, ...partial });
  }

  return (
    <div className="w-56 shrink-0 space-y-5">
      <div className="card space-y-4">
        {/* Identity Search — find sessions by email, phone, or customer ID */}
        <div>
          <label className="mb-2 block text-xs font-semibold uppercase tracking-wider text-gray-500">
            Find by user
          </label>
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="email, phone, or ID"
              value={filters.identityQuery}
              onChange={(e) => update({ identityQuery: e.target.value })}
              className="input pl-8 pr-8 text-sm"
              autoComplete="off"
              spellCheck={false}
            />
            {filters.identityQuery && (
              <button
                type="button"
                onClick={() => update({ identityQuery: '' })}
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-0.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                title="Clear"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
          <p className="mt-1 text-[11px] leading-tight text-gray-400">
            Values are hashed locally before search — emails and phones never leave the browser in plain text.
          </p>
        </div>

        {/* Date Range */}
        <div>
          <label className="mb-2 block text-xs font-semibold uppercase tracking-wider text-gray-500">
            Date Range
          </label>
          <div className="space-y-1">
            {DATE_RANGES.filter((r) => r.value !== 'custom').map((range) => (
              <button
                key={range.value}
                onClick={() => update({ dateRange: range.value })}
                className={clsx(
                  'flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm transition-colors',
                  filters.dateRange === range.value
                    ? 'bg-brand-50 text-brand-700 font-medium'
                    : 'text-gray-600 hover:bg-gray-50',
                )}
              >
                <Calendar className="h-4 w-4" />
                {range.label}
              </button>
            ))}
          </div>
        </div>

        {/* Device Type */}
        <div>
          <label className="mb-2 block text-xs font-semibold uppercase tracking-wider text-gray-500">
            Device
          </label>
          <div className="space-y-1">
            {DEVICE_TYPES.map((device) => {
              const icons: Record<string, React.ReactNode> = {
                all: <Monitor className="h-4 w-4" />,
                desktop: <Monitor className="h-4 w-4" />,
                mobile: <Smartphone className="h-4 w-4" />,
                tablet: <Tablet className="h-4 w-4" />,
              };
              return (
                <button
                  key={device.value}
                  onClick={() => update({ device: device.value })}
                  className={clsx(
                    'flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm transition-colors',
                    filters.device === device.value
                      ? 'bg-brand-50 text-brand-700 font-medium'
                      : 'text-gray-600 hover:bg-gray-50',
                  )}
                >
                  {icons[device.value]}
                  {device.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Toggles */}
        <div>
          <label className="mb-2 block text-xs font-semibold uppercase tracking-wider text-gray-500">
            Filters
          </label>
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm text-gray-600">
              <input
                type="checkbox"
                checked={filters.hasErrors}
                onChange={(e) => update({ hasErrors: e.target.checked })}
                className="rounded border-gray-300 text-brand-600 focus:ring-brand-500"
              />
              Has errors
            </label>
            <label className="flex items-center gap-2 text-sm text-gray-600">
              <input
                type="checkbox"
                checked={filters.hasRageClicks}
                onChange={(e) => update({ hasRageClicks: e.target.checked })}
                className="rounded border-gray-300 text-brand-600 focus:ring-brand-500"
              />
              Has rage clicks
            </label>
          </div>
        </div>

        {/* URL Filter */}
        <div>
          <label className="mb-2 block text-xs font-semibold uppercase tracking-wider text-gray-500">
            Page URL
          </label>
          <input
            type="text"
            placeholder="e.g. /checkout"
            value={filters.url}
            onChange={(e) => update({ url: e.target.value })}
            className="input"
          />
        </div>
      </div>
    </div>
  );
}
